import config from "config";
import { ApolloClient } from "apollo-client";
import { InMemoryCache } from "apollo-cache-inmemory";
import { HttpLink } from "apollo-link-http";
import _ from "lodash";
import { AuthDriver as auth } from "auth";
import AvailableSeasonsDataProvider from "./AvailableSeasonsDataProvider";
import SportCodesDataProvider from "./SportCodesDataProvider";
import SchoolsDataProvider from "./SchoolsDataProvider";
import ComboLocationsDataProvider from "./ComboLocationsDataProvider";
import TeamsDataProvider from "./TeamsDataProvider";
import SeasonTeamsDataProvider from "./SeasonTeamsDataProvider";
import DistrictsDataProvider from "./DistrictsDataProvider";
import GamesDataProvider from "./GamesDataProvider";
import GameStatsDataProvider from "./GameStatsDataProvider";
import LocationsDataProvider from "./LocationsDataProvider";
import PlayersDataProvider from "./PlayersDataProvider";
import PeopleDataProvider from "./PeopleDataProvider";
import GamePlayersDataProvider from "./GamePlayersDataProvider";
import SportSeasonsDataProvider from "./SportSeasonsDataProvider";
import NewsgateTransformsDataProvider from "./NewsgateTransformsDataProvider";

// client singleton
//
let client = null;

function initClient() {
  if (client) return;
  const httpLink = new HttpLink({
    uri: config("backend.apiUri"),
    fetch: (uri, options) =>
      auth.getCurrentUser().then(({ session }) => {
        const jwtAccessToken = _.get(session, "accessToken.jwtToken");
        options.headers.authorization = `Bearer ${jwtAccessToken}`;
        return window.fetch(uri, options);
      })
  });

  client = new ApolloClient({
    cache: new InMemoryCache({
      dataIdFromObject: (o) => (o.id ? o.id : o.stableId)
    }),
    link: httpLink,
    defaultOptions: {
      fetchPolicy: "no-cache",
      watchQuery: {
        fetchPolicy: "no-cache"
      },
      query: {
        fetchPolicy: "no-cache"
      }
    }
  });

  return client;
}

class UnionDataProvider {
  constructor(client) {
    //console.log("UnionDataProvider()");
    const gamesDataProvider = new GamesDataProvider(client);
    this.providers = {
      availableSeasons: new AvailableSeasonsDataProvider(client),
      sportCodes: new SportCodesDataProvider(client),
      schools: new SchoolsDataProvider(client),
      comboLocations: new ComboLocationsDataProvider(client),
      teams: new TeamsDataProvider(client),
      seasonTeams: new SeasonTeamsDataProvider(client),
      districts: new DistrictsDataProvider(client),
      games: gamesDataProvider,
      gamestats: new GameStatsDataProvider(client, gamesDataProvider),
      locations: new LocationsDataProvider(client),
      players: new PlayersDataProvider(client),
      people: new PeopleDataProvider(client),
      gamePlayers: new GamePlayersDataProvider(client),
      sportSeasons: new SportSeasonsDataProvider(client),
      newsgateTransforms: new NewsgateTransformsDataProvider(client)
    };
  }

  getProvider(resource) {
    const provider = this.providers[resource];
    if (!provider) throw new Error("NO PROVIDER REGISTERED:" + resource);
    return provider;
  }

  async getList(resource, params) {
    //console.log("UnionDataProvider.getList(" + resource + "," + JSON.stringify(params) + ")");
    return await this.getProvider(resource).getList(resource, params);
  }

  async getOne(resource, params) {
    //console.log("unionDataProvider.getOne(" + resource + "," + JSON.stringify(params) + ")");
    return await this.getProvider(resource).getOne(resource, params);
  }

  async getMany(resource, params) {
    //console.log("unionDataProvider.getMany(" + resource + "," + JSON.stringify(params) + ")");
    return await this.getProvider(resource).getMany(resource, params);
  }

  async getManyReference(resource, params) {
    //console.log("unionDataProvider.getManyReference(" + resource + "," + JSON.stringify(params) + ")");
    return await this.getProvider(resource).getManyReference(resource, params);
  }

  async update(resource, params) {
    //console.log("unionDataProvider.update(" + resource + "," + JSON.stringify(params) + ")");
    return await this.getProvider(resource).update(resource, params);
  }

  async updateMany(resource, params) {
    //console.log("unionDataProvider.updateMany(" + resource + "," + JSON.stringify(params) + ")");
    return await this.getProvider(resource).updateMany(resource, params);
  }

  async create(resource, params) {
    //console.log("unionDataProvider.create(" + resource + "," + JSON.stringify(params) + ")");
    return await this.getProvider(resource).create(resource, params);
  }

  async delete(resource, params) {
    //console.log("unionDataProvider.delete(" + resource + "," + JSON.stringify(params) + ")");
    return await this.getProvider(resource).delete(resource, params);
  }

  async deleteMany(resource, params) {
    //console.log("unionDataProvider.deleteMany(" + resource + "," + JSON.stringify(params) + ")");
    return await this.getProvider(resource).deleteMany(resource, params);
  }
}

//
// react-admin expects an object with functions, not a javascript
// class (it calls directly into the functions using reflection and
// doesn't always include a 'this', so a real class won't work). this
// wrapper just creates a real js class object and wraps it up so
// react-admin can work with it.
//

const unionDataProvider = new UnionDataProvider(initClient());

const unionDataProviderWrapper = {
  getList: async function (resource, params) {
    return await unionDataProvider.getList(resource, params);
  },
  getOne: async function (resource, params) {
    return await unionDataProvider
      .getProvider(resource)
      .getOne(resource, params);
  },
  getMany: async function (resource, params) {
    return await unionDataProvider.getMany(resource, params);
  },
  getManyReference: async function (resource, params) {
    return await unionDataProvider.getManyReference(resource, params);
  },
  update: async function (resource, params) {
    return await unionDataProvider.update(resource, params);
  },
  updateMany: async function (resource, params) {
    return await unionDataProvider.updateMany(resource, params);
  },
  create: async function (resource, params) {
    return await unionDataProvider.create(resource, params);
  },
  delete: async function (resource, params) {
    return await unionDataProvider.delete(resource, params);
  },
  deleteMany: async function (resource, params) {
    return await unionDataProvider.deleteMany(resource, params);
  },
  getBaseProvider: function () {
    return unionDataProvider;
  }
};

export default unionDataProviderWrapper;
